import React from 'react';
import {
  Box,
  MenuItem,
  Select,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import DarkModeIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeIcon from '@mui/icons-material/LightModeOutlined';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import { useThemeMode } from 'shared/hooks';
import {
  TIER_OPTIONS,
  LIGHT_THEME_OPTIONS,
  DARK_THEME_OPTIONS,
} from 'shared/constants/theme';

/**
 * A brightness toggle whose tooltip also opens on keyboard focus, so the Tooltip wraps the
 * button, not the icon. Forwards its ref and props: `ToggleButtonGroup` clones its
 * children to inject `selected` and `onChange`.
 */
const TierToggle = React.forwardRef((props, ref) => {
  const { label, icon: Icon, ...toggleProps } = props;
  return (
    <Tooltip title={label} arrow placement="bottom">
      <ToggleButton {...toggleProps} ref={ref} aria-label={label}>
        <Icon sx={{ fontSize: '1.05rem' }} />
      </ToggleButton>
    </Tooltip>
  );
});
TierToggle.displayName = 'TierToggle';

const MODE_CHOICES = [
  { value: 'light', label: 'Light', Icon: LightModeIcon },
  { value: 'system', label: 'Auto', Icon: SettingsBrightnessIcon },
  { value: 'dark', label: 'Dark', Icon: DarkModeIcon },
];

// One type size for this whole panel
const LABEL_SIZE = '0.875rem';

const groupSx = {
  width: '100%',
  '& .MuiToggleButton-root': {
    flex: 1,
    textTransform: 'none',
    fontSize: LABEL_SIZE,
    lineHeight: 1.2,
    paddingTop: '6px',
    paddingBottom: '6px',
    gap: '6px',
  },
};

const labelSx = {
  display: 'block',
  color: 'text.secondary',
  fontSize: LABEL_SIZE,
  mt: 1.5,
  mb: 0.75,
};

// No floating label, so the app-wide notch size doesn't apply; only the text is sized.
const selectSx = {
  fontSize: LABEL_SIZE,
  '& .MuiSelect-select': { fontSize: LABEL_SIZE },
};

/**
 * A theme option drawn as that family's own field: its well and control border, plus an accent
 * border and check when chosen. The field carries hover, focus and selection, so MenuItem's
 * own fills are cleared. The second border pixel is an inset shadow, so nothing shifts.
 */
function themeFieldSx(field, selected) {
  const edge = selected ? field.accent : field.border;
  const thick = `inset 0 0 0 1px ${edge}`;
  return {
    mx: 1,
    my: 0.5,
    px: 1.25,
    py: 0.75,
    minHeight: 0,
    borderRadius: '4px',
    fontSize: LABEL_SIZE,
    fontWeight: selected ? 600 : undefined,
    bgcolor: field.well,
    border: `1px solid ${edge}`,
    boxShadow: selected ? thick : 'none',
    '&:hover, &.Mui-focusVisible, &.Mui-selected, &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible': {
      bgcolor: field.well,
    },
    '&:hover, &.Mui-focusVisible': { boxShadow: thick },
  };
}

/**
 * Appearance settings in the profile dropdown: mode, family, and tier. The family list
 * follows the active scheme.
 */
export default function AppearanceMenu() {
  const {
    mode, setMode, resolvedMode, themeFamily, setThemeFamily, previewThemeFamily, tier, setTier,
    themeFields,
  } = useThemeMode();

  const themeOptions = resolvedMode === 'dark' ? DARK_THEME_OPTIONS : LIGHT_THEME_OPTIONS;

  return (
    <Box sx={{ px: 2, py: 1.25, minWidth: 244 }}>
      <Typography
        variant="caption"
        sx={{ ...labelSx, mt: 0, fontWeight: 600 }}
      >
        Appearance
      </Typography>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={mode}
        onChange={(event, next) => { if (next) setMode(next); }}
        aria-label="Color mode"
        sx={groupSx}
      >
        {MODE_CHOICES.map(({ value, label, Icon }) => (
          <ToggleButton key={value} value={value} aria-label={label}>
            <Icon sx={{ fontSize: '1.05rem' }} />
            {label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <Typography variant="caption" sx={labelSx}>Brightness</Typography>
      <ToggleButtonGroup
        size="small"
        exclusive
        value={tier}
        onChange={(event, next) => { if (next) setTier(next); }}
        aria-label="Brightness"
        sx={groupSx}
      >
        {TIER_OPTIONS.map(({ value, label, icon }) => (
          <TierToggle key={value} value={value} label={label} icon={icon} />
        ))}
      </ToggleButtonGroup>
      <Typography variant="caption" sx={labelSx}>Theme</Typography>
      {/* Pointing at or arrowing to a theme previews it across the app; choosing keeps it,
          and closing without choosing puts the saved theme back. */}
      <Select
        size="small"
        fullWidth
        value={themeFamily}
        onChange={(event) => setThemeFamily(event.target.value)}
        onClose={() => previewThemeFamily(null)}
        aria-label="Theme"
        renderValue={(value) => themeOptions.find((o) => o.value === value)?.label}
        MenuProps={{
          // React bubbles portal events up the component tree, so without this the profile
          // menu around us also handles the arrow keys and pulls focus out of this list.
          onKeyDown: (event) => event.stopPropagation(),
          slotProps: { list: { onMouseLeave: () => previewThemeFamily(null) } },
        }}
        sx={{
          ...selectSx,
          // The closed field shows the saved theme, even while the app previews another.
          ...(themeFields[themeFamily] && {
            bgcolor: themeFields[themeFamily].well,
            '& .MuiOutlinedInput-notchedOutline': { borderColor: themeFields[themeFamily].border },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: themeFields[themeFamily].accent,
            },
          }),
        }}
      >
        {themeOptions.map(({ value, label }) => (
          <MenuItem
            key={value}
            value={value}
            onMouseEnter={() => previewThemeFamily(value)}
            onFocus={() => previewThemeFamily(value)}
            sx={themeFields[value]
              ? themeFieldSx(themeFields[value], value === themeFamily)
              : { fontSize: LABEL_SIZE }}
          >
            {label}
            {value === themeFamily && (
              <CheckIcon sx={{ ml: 'auto', fontSize: '1.1rem', color: themeFields[value]?.accent }} />
            )}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
}
